/* External dependencies */
import { KeyIcon } from "@heroicons/react/20/solid";
import { Turnstile } from "@marsidev/react-turnstile";
import PropTypes from "prop-types";
import React from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { NavLink } from "react-router-dom";
/* Internal dependencies */
import ErrorDisplay, { ErrorPropTypeShape } from "../../../src/errors/ErrorDisplay";
import { TextField, Checkbox, Button, Link } from "@yoast/ui-library";
import * as styles from "./LoginFormStyles.module.scss";

// Messages
const messages = defineMessages( {
	labelEmail: {
		id: "signup.email",
		defaultMessage: "Email address",
	},
	labelPassword: {
		id: "signup.password",
		defaultMessage: "Password",
	},
	labelOTP: {
		id: "signup.otp",
		defaultMessage: "Verification code",
	},
	loginButton: {
		id: "login.login",
		defaultMessage: "Log in",
	},
	passkeyLogin: {
		id: "login.passkey",
		defaultMessage: "Sign in with a passkey",
	},
	rememberMe: {
		id: "login.rememberMe",
		defaultMessage: "Remember me",
	},
	forgotPassword: {
		id: "login.forgotPassword",
		defaultMessage: "Forgot your password?",
	},
	loading: {
		id: "login.loading",
		defaultMessage: "Logging in...",
	},
} );

/**
 * The passkey sign-in button and its "or" divider. Rendered only when a passkey handler is
 * provided; extracted so LoginForm's render stays within the complexity budget.
 *
 * @param {Object} props The component props.
 *
 * @returns {ReactElement|null} The passkey option, or null when no handler is provided.
 */
const PasskeyLoginOption = ( { onPasskeyLogin = null, disabled = false } ) => {
	if ( ! onPasskeyLogin ) {
		return null;
	}
	return (
		<>
			<div>
				<Button
					className={ styles.passkeyButton }
					variant="secondary"
					type="button"
					onClick={ onPasskeyLogin }
					disabled={ disabled }
				>
					<KeyIcon className={ styles.passkeyIcon } />
					<FormattedMessage { ...messages.passkeyLogin } />
				</Button>
			</div>
			<div className={ styles.passkeyDivider }>
				<hr className={ styles.dividerLine } />
				<span className={ styles.dividerText }>
					<FormattedMessage id="login.or" defaultMessage="or" />
				</span>
				<hr className={ styles.dividerLine } />
			</div>
		</>
	);
};

PasskeyLoginOption.propTypes = {
	onPasskeyLogin: PropTypes.func,
	disabled: PropTypes.bool,
};

/**
 * The one-time password field, shown only once a 2FA code is required.
 *
 * @param {Object} props The component props.
 *
 * @returns {ReactElement|null} The OTP field, or null when not required.
 */
const OtpField = ( { show = false, label, onChange } ) => {
	if ( ! show ) {
		return null;
	}
	return (
		<div>
			<TextField
				id="otp"
				name="otp"
				type="text"
				inputMode="numeric"
				autoComplete="one-time-code"
				label={ label }
				onChange={ onChange }
			/>
		</div>
	);
};

OtpField.propTypes = {
	show: PropTypes.bool,
	label: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
};

/**
 * Login component for logging in to MyYoast.
 *
 * @param {Object} props the props.
 *
 * @returns {ReactElement} The login form element.
 */
const LoginForm = ( props ) => {
	// When Turnstile is bypassed (automated testing), the pending/error states should not gate the
	// button so the form can be submitted without waiting for the widget.
	const turnstileActive = ! props.turnstileBypassed;
	const busy            = props.loading || ( turnstileActive && props.turnstilePending );
	const missingCredentials = ! props.email || ! props.password;
	const disabled        = busy || missingCredentials || ( turnstileActive && props.turnstileError );
	const buttonText      = busy ? messages.loading : messages.loginButton;
	return (
		<form onSubmit={ props.handleSubmit } className={ styles.form }>
			<ErrorDisplay error={ props.error } />

			<PasskeyLoginOption
				onPasskeyLogin={ props.onPasskeyLogin }
				disabled={ busy || props.turnstileError }
			/>

			<div>
				<TextField
					id="email-address"
					name="email"
					type="text"
					autoComplete="email"
					label={ props.intl.formatMessage( messages.labelEmail ) }
					value={ props.email }
					onChange={ props.onUpdateEmail }
				/>
			</div>

			<div>
				<TextField
					id="password"
					name="password"
					type="password"
					autoComplete="current-password"
					label={ props.intl.formatMessage( messages.labelPassword ) }
					onChange={ props.onUpdatePassword }
				/>
			</div>

			<OtpField
				show={ props.requireOTP }
				label={ props.intl.formatMessage( messages.labelOTP ) }
				onChange={ props.onUpdateOTP }
			/>

			<div className={ styles.rememberForgot }>
				<Checkbox
					id="remember-me"
					name="remember-me"
					label={ props.intl.formatMessage( messages.rememberMe ) }
					onChange={ props.onRememberCheck }
					value=""
					checked={ props.rememberMe }
				/>
				<Link
					as={ NavLink }
					to="/forgot-password"
				>
					<FormattedMessage { ...messages.forgotPassword } />
				</Link>
			</div>

			{
				props.turnstileSiteKey &&
				<div data-turnstile-verified={ String( props.turnstileVerified ) }>
					<Turnstile
						ref={ props.turnstileRef }
						siteKey={ props.turnstileSiteKey }
						options={ { action: "login", theme: "light", size: "flexible" } }
						scriptOptions={ { onError: props.onTurnstileScriptError } }
						onSuccess={ props.onTurnstileVerify }
						onExpire={ props.onTurnstileExpire }
						onError={ props.onTurnstileError }
						onTimeout={ props.onTurnstileTimeout }
					/>
				</div>
			}

			<div>
				<Button
					className={ styles.button }
					type="submit"
					disabled={ disabled }
					aria-disabled={ disabled }
				>
					<FormattedMessage { ...buttonText } />
				</Button>
			</div>
		</form>
	);
};

LoginForm.propTypes = {
	intl: intlShape.isRequired,
	rememberMe: PropTypes.bool,
	email: PropTypes.string,
	password: PropTypes.string,
	error: ErrorPropTypeShape,
	onUpdateEmail: PropTypes.func.isRequired,
	onUpdatePassword: PropTypes.func.isRequired,
	onUpdateOTP: PropTypes.func.isRequired,
	onRememberCheck: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	onPasskeyLogin: PropTypes.func,
	loading: PropTypes.bool,
	turnstileBypassed: PropTypes.bool,
	turnstilePending: PropTypes.bool,
	requireOTP: PropTypes.bool,
	turnstileSiteKey: PropTypes.string,
	turnstileRef: PropTypes.object,
	turnstileError: PropTypes.bool,
	turnstileVerified: PropTypes.bool,
	onTurnstileVerify: PropTypes.func,
	onTurnstileExpire: PropTypes.func,
	onTurnstileError: PropTypes.func,
	onTurnstileTimeout: PropTypes.func,
	onTurnstileScriptError: PropTypes.func,
};

LoginForm.defaultProps = {
	rememberMe: false,
	email: "",
	password: "",
	error: null,
	onPasskeyLogin: null,
	loading: false,
	turnstileBypassed: false,
	turnstilePending: false,
	requireOTP: false,
	turnstileSiteKey: "",
	turnstileRef: null,
	turnstileError: false,
	turnstileVerified: false,
	onTurnstileVerify: () => {},
	onTurnstileExpire: () => {},
	onTurnstileError: () => {},
	onTurnstileTimeout: () => {},
	onTurnstileScriptError: () => {},
};

export default injectIntl( LoginForm );
